import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  DollarSign,
  TrendingDown,
  CreditCard,
  PiggyBank,
  Plus,
  User,
  Calendar,
  ArrowLeft,
  Loader2,
  Edit,
  Trash2,
  Check,
  ChevronDown,
  ChevronUp,
  Calculator,
  FileText,
  Receipt
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFinance() {
  const { token, isAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [debts, setDebts] = useState([]);
  const [taxSummary, setTaxSummary] = useState(null);
  const [taxEntries, setTaxEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingExpense, setAddingExpense] = useState(false);
  const [addingDebt, setAddingDebt] = useState(false);
  const [addingTax, setAddingTax] = useState(false);
  const [expandedDebt, setExpandedDebt] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentData, setEditPaymentData] = useState({});
  const [addDebtOpen, setAddDebtOpen] = useState(false);
  const [addTaxOpen, setAddTaxOpen] = useState(false);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(new Date().getFullYear());
  
  const [newExpense, setNewExpense] = useState({
    owner: '',
    category: 'operating',
    description: '',
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    is_recurring: false
  });
  
  const [newDebt, setNewDebt] = useState({
    creditor: '',
    description: '',
    reason: '',
    total_amount: 0,
    monthly_payment: 0,
    num_payments: 1,
    start_date: new Date().toISOString().split('T')[0]
  });

  const [newTax, setNewTax] = useState({
    entry_type: 'collected',
    description: '',
    subtotal: 0,
    date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });

  // Calcul automatique du calendrier prévu
  const previewSchedule = useMemo(() => {
    if (!newDebt.total_amount || !newDebt.monthly_payment || !newDebt.num_payments) return [];
    
    const schedule = [];
    let remaining = newDebt.total_amount;
    const startDate = new Date(newDebt.start_date);
    
    for (let i = 0; i < newDebt.num_payments; i++) {
      const paymentDate = new Date(startDate);
      paymentDate.setMonth(paymentDate.getMonth() + i + 1);
      
      let amount;
      if (i === newDebt.num_payments - 1) {
        amount = Math.round(remaining * 100) / 100;
      } else {
        amount = Math.min(newDebt.monthly_payment, remaining);
      }
      
      remaining -= amount;
      
      schedule.push({
        payment_number: i + 1,
        amount: amount,
        due_date: paymentDate.toISOString().split('T')[0]
      });
    }
    
    return schedule;
  }, [newDebt.total_amount, newDebt.monthly_payment, newDebt.num_payments, newDebt.start_date]);

  // Tax preview calculation
  const taxPreview = useMemo(() => {
    const subtotal = newTax.subtotal || 0;
    const tps = Math.round(subtotal * 0.05 * 100) / 100;
    const tvq = Math.round(subtotal * 0.09975 * 100) / 100;
    return { tps, tvq, total: Math.round((tps + tvq) * 100) / 100 };
  }, [newTax.subtotal]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isAdmin()) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    if (token) {
      fetchTaxSummary();
    }
  }, [selectedFiscalYear, token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [budgetsRes, expensesRes, debtsRes, taxesRes] = await Promise.all([
        axios.get(`${API}/admin/finance/budgets`, { headers }),
        axios.get(`${API}/admin/finance/expenses`, { headers }),
        axios.get(`${API}/admin/finance/debts`, { headers }),
        axios.get(`${API}/admin/finance/taxes`, { headers })
      ]);
      setBudgets(budgetsRes.data);
      setExpenses(expensesRes.data);
      setDebts(debtsRes.data);
      setTaxEntries(taxesRes.data);
      fetchTaxSummary();
    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxSummary = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/finance/taxes/summary?fiscal_year=${selectedFiscalYear}`, { headers });
      setTaxSummary(response.data);
    } catch (error) {
      console.error('Error fetching tax summary:', error);
    }
  };

  const handleAddExpense = async () => {
    try {
      setAddingExpense(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/finance/expenses`, newExpense, { headers });
      toast.success('Dépense ajoutée');
      fetchData();
      setNewExpense({ owner: '', category: 'operating', description: '', amount: 0, date: new Date().toISOString().split('T')[0], is_recurring: false });
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setAddingExpense(false);
    }
  };

  const handleAddDebt = async () => {
    if (!newDebt.creditor || !newDebt.total_amount || !newDebt.monthly_payment) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      setAddingDebt(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/finance/debts`, newDebt, { headers });
      toast.success('Dette ajoutée avec le calendrier de paiements');
      fetchData();
      setAddDebtOpen(false);
      setNewDebt({ creditor: '', description: '', reason: '', total_amount: 0, monthly_payment: 0, num_payments: 1, start_date: new Date().toISOString().split('T')[0] });
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setAddingDebt(false);
    }
  };

  const handleAddTax = async () => {
    if (!newTax.description || !newTax.subtotal) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    try {
      setAddingTax(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/finance/taxes`, {
        ...newTax,
        tps_amount: taxPreview.tps,
        tvq_amount: taxPreview.tvq
      }, { headers });
      toast.success('Entrée de taxe ajoutée');
      fetchData();
      setAddTaxOpen(false);
      setNewTax({ entry_type: 'collected', description: '', subtotal: 0, date: new Date().toISOString().split('T')[0], reference: '', notes: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setAddingTax(false);
    }
  };

  const handleUpdatePayment = async (debtId, paymentNumber) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/finance/debts/${debtId}/payments/${paymentNumber}`, editPaymentData, { headers });
      toast.success('Paiement mis à jour');
      fetchData();
      setEditingPayment(null);
      setEditPaymentData({});
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleMarkPaymentPaid = async (debtId, paymentNumber, amount) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/finance/debts/${debtId}/payments/${paymentNumber}`, {
        status: 'paid',
        paid_amount: amount,
        paid_date: new Date().toISOString()
      }, { headers });
      toast.success('Paiement marqué comme payé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeletePayment = async (debtId, paymentNumber) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce versement?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/finance/debts/${debtId}/payments/${paymentNumber}`, { headers });
      toast.success('Versement supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteDebt = async (debtId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette dette?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/finance/debts/${debtId}`, { headers });
      toast.success('Dette supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteTax = async (taxId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette entrée?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/finance/taxes/${taxId}`, { headers });
      toast.success('Entrée supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-CA');
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalDebts = debts.reduce((sum, d) => sum + (d.remaining_balance || d.total_amount || 0), 0);
  const totalBudgets = budgets.reduce((sum, b) => sum + (b.amount || 0), 0);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin()) return null;

  // Generate fiscal years for selector
  const currentYear = new Date().getFullYear();
  const fiscalYears = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="min-h-screen py-8 bg-gray-50" data-testid="admin-finance">
      <div className="container mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {/* Header */}
          <div className="mb-8">
            <Link to="/admin" className="inline-flex items-center text-gray-500 hover:text-black mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au tableau de bord
            </Link>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-black">Gestion Financière</h1>
                <p className="text-gray-500">Dettes, dépenses et taxes</p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-4 w-4 text-red-600" />
                  <span className="text-gray-500 text-sm">Dettes</span>
                </div>
                <p className="text-2xl font-bold text-black font-mono">{formatPrice(totalDebts)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="h-4 w-4 text-gray-600" />
                  <span className="text-gray-500 text-sm">Dépenses</span>
                </div>
                <p className="text-2xl font-bold text-black font-mono">{formatPrice(totalExpenses)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Receipt className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-500 text-sm">Taxes à payer</span>
                </div>
                <p className="text-2xl font-bold text-black font-mono">{formatPrice(taxSummary?.totals?.net_total || 0)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <PiggyBank className="h-4 w-4 text-green-600" />
                  <span className="text-gray-500 text-sm">Budgets</span>
                </div>
                <p className="text-2xl font-bold text-black font-mono">{formatPrice(totalBudgets)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="debts" className="space-y-6">
            <TabsList className="bg-white border border-gray-200">
              <TabsTrigger value="debts" className="data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-debts">
                <CreditCard className="h-4 w-4 mr-2" />
                Dettes
              </TabsTrigger>
              <TabsTrigger value="taxes" className="data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-taxes">
                <Receipt className="h-4 w-4 mr-2" />
                Taxes QC
              </TabsTrigger>
              <TabsTrigger value="expenses" className="data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-expenses">
                <TrendingDown className="h-4 w-4 mr-2" />
                Dépenses
              </TabsTrigger>
              <TabsTrigger value="budgets" className="data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-budgets">
                <PiggyBank className="h-4 w-4 mr-2" />
                Budgets
              </TabsTrigger>
            </TabsList>

            {/* Debts Tab */}
            <TabsContent value="debts">
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-black">Gestion des Dettes</CardTitle>
                  <Dialog open={addDebtOpen} onOpenChange={setAddDebtOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-black text-white hover:bg-gray-800" data-testid="add-debt-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Nouvelle dette
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-black">Ajouter une dette avec entente de paiement</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-6 py-4">
                        <div className="space-y-4">
                          <h3 className="font-semibold text-black border-b pb-2">Informations de la dette</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-gray-700">Créancier *</Label>
                              <Input value={newDebt.creditor} onChange={(e) => setNewDebt({ ...newDebt, creditor: e.target.value })} className="bg-white border-gray-300 text-black" placeholder="Nom du créancier" />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-700">Montant total *</Label>
                              <Input type="number" step="0.01" value={newDebt.total_amount || ''} onChange={(e) => setNewDebt({ ...newDebt, total_amount: parseFloat(e.target.value) || 0 })} className="bg-white border-gray-300 text-black" placeholder="0.00" />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700">Description</Label>
                            <Input value={newDebt.description} onChange={(e) => setNewDebt({ ...newDebt, description: e.target.value })} className="bg-white border-gray-300 text-black" placeholder="Description courte" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700">Raison de la dette</Label>
                            <Textarea value={newDebt.reason} onChange={(e) => setNewDebt({ ...newDebt, reason: e.target.value })} className="bg-white border-gray-300 text-black" placeholder="Expliquez pourquoi cette dette a été contractée..." rows={3} />
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h3 className="font-semibold text-black border-b pb-2">Entente de paiement</h3>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="text-gray-700">Paiement mensuel *</Label>
                              <Input type="number" step="0.01" value={newDebt.monthly_payment || ''} onChange={(e) => setNewDebt({ ...newDebt, monthly_payment: parseFloat(e.target.value) || 0 })} className="bg-white border-gray-300 text-black" placeholder="0.00" />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-700">Nombre de mois</Label>
                              <Input type="number" min="1" value={newDebt.num_payments || ''} onChange={(e) => setNewDebt({ ...newDebt, num_payments: parseInt(e.target.value) || 1 })} className="bg-white border-gray-300 text-black" />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-700">Date de début</Label>
                              <Input type="date" value={newDebt.start_date} onChange={(e) => setNewDebt({ ...newDebt, start_date: e.target.value })} className="bg-white border-gray-300 text-black" />
                            </div>
                          </div>
                          
                          {newDebt.monthly_payment > 0 && newDebt.num_payments > 0 && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex items-center gap-2 mb-3">
                                <Calculator className="h-4 w-4 text-gray-600" />
                                <span className="font-medium text-gray-700">Calcul automatique</span>
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Total des paiements réguliers:</span>
                                  <span className="ml-2 font-mono">{formatPrice(newDebt.monthly_payment * (newDebt.num_payments - 1))}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Dernier paiement:</span>
                                  <span className="ml-2 font-mono font-semibold">{formatPrice(newDebt.total_amount - (newDebt.monthly_payment * (newDebt.num_payments - 1)))}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {previewSchedule.length > 0 && (
                          <div className="space-y-4">
                            <h3 className="font-semibold text-black border-b pb-2">Calendrier des paiements prévu</h3>
                            <div className="max-h-48 overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100 sticky top-0">
                                  <tr>
                                    <th className="text-left p-2 text-gray-600">#</th>
                                    <th className="text-left p-2 text-gray-600">Date</th>
                                    <th className="text-right p-2 text-gray-600">Montant</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {previewSchedule.map((payment, idx) => (
                                    <tr key={idx} className={`border-b ${idx === previewSchedule.length - 1 ? 'bg-yellow-50' : ''}`}>
                                      <td className="p-2 text-gray-700">{payment.payment_number}</td>
                                      <td className="p-2 text-gray-700">{formatDate(payment.due_date)}</td>
                                      <td className="p-2 text-right font-mono text-black">
                                        {formatPrice(payment.amount)}
                                        {idx === previewSchedule.length - 1 && payment.amount !== newDebt.monthly_payment && (
                                          <span className="ml-2 text-xs text-yellow-600">(ajusté)</span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" className="border-gray-300">Annuler</Button>
                        </DialogClose>
                        <Button onClick={handleAddDebt} disabled={addingDebt} className="bg-black text-white hover:bg-gray-800">
                          {addingDebt && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Créer la dette
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-3">{[...Array(3)].map((_, i) => (<Skeleton key={i} className="h-24 bg-gray-100" />))}</div>
                  ) : debts.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Aucune dette enregistrée</p>
                  ) : (
                    <div className="space-y-4">
                      {debts.map((debt) => (
                        <div key={debt.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setExpandedDebt(expandedDebt === debt.id ? null : debt.id)}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                  <CreditCard className="h-5 w-5 text-red-700" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-black">{debt.creditor}</h3>
                                  <p className="text-gray-500 text-sm">{debt.description}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <p className="font-mono font-semibold text-black">{formatPrice(debt.remaining_balance || debt.total_amount)}</p>
                                  <p className="text-xs text-gray-500">sur {formatPrice(debt.total_amount)} • {debt.num_payments || 0} paiements</p>
                                </div>
                                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDeleteDebt(debt.id); }}>
                                  <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-600" />
                                </Button>
                                {expandedDebt === debt.id ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                              </div>
                            </div>
                            <div className="mt-3">
                              <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>Payé: {formatPrice(debt.total_paid || 0)}</span>
                                <span>{Math.round(((debt.total_paid || 0) / (debt.total_amount || 1)) * 100)}%</span>
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full bg-green-500 transition-all" style={{ width: `${((debt.total_paid || 0) / (debt.total_amount || 1)) * 100}%` }} />
                              </div>
                            </div>
                          </div>
                          
                          {expandedDebt === debt.id && (
                            <div className="p-4 border-t border-gray-200">
                              {debt.reason && (
                                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                  <p className="text-xs text-gray-500 uppercase mb-1">Raison de la dette</p>
                                  <p className="text-gray-700">{debt.reason}</p>
                                </div>
                              )}
                              
                              <h4 className="font-medium text-black mb-3">Calendrier des paiements</h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="text-left p-2 text-gray-600">#</th>
                                      <th className="text-left p-2 text-gray-600">Date</th>
                                      <th className="text-right p-2 text-gray-600">Montant</th>
                                      <th className="text-center p-2 text-gray-600">Statut</th>
                                      <th className="text-left p-2 text-gray-600">Note</th>
                                      <th className="text-right p-2 text-gray-600">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(debt.payment_schedule || []).map((payment) => (
                                      <tr key={payment.payment_number} className="border-b border-gray-100">
                                        <td className="p-2 text-gray-700">{payment.payment_number}</td>
                                        <td className="p-2">
                                          {editingPayment === `${debt.id}-${payment.payment_number}` ? (
                                            <Input type="date" defaultValue={payment.due_date?.split('T')[0]} className="w-36 h-8" onChange={(e) => setEditPaymentData({ ...editPaymentData, due_date: e.target.value })} />
                                          ) : (
                                            <span className="text-gray-700">{formatDate(payment.due_date)}</span>
                                          )}
                                        </td>
                                        <td className="p-2 text-right">
                                          {editingPayment === `${debt.id}-${payment.payment_number}` ? (
                                            <Input type="number" step="0.01" defaultValue={payment.amount} className="w-24 h-8 text-right" onChange={(e) => setEditPaymentData({ ...editPaymentData, amount: parseFloat(e.target.value) })} />
                                          ) : (
                                            <span className="font-mono text-black">{formatPrice(payment.amount)}</span>
                                          )}
                                        </td>
                                        <td className="p-2 text-center">
                                          {payment.status === 'paid' ? (
                                            <Badge className="bg-green-100 text-green-800 border border-green-200"><Check className="h-3 w-3 mr-1" /> Payé</Badge>
                                          ) : (
                                            <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">En attente</Badge>
                                          )}
                                        </td>
                                        <td className="p-2">
                                          {editingPayment === `${debt.id}-${payment.payment_number}` ? (
                                            <Input defaultValue={payment.notes || ''} placeholder="Note..." className="h-8" onChange={(e) => setEditPaymentData({ ...editPaymentData, notes: e.target.value })} />
                                          ) : (
                                            <span className="text-gray-500 text-xs">{payment.notes || '-'}</span>
                                          )}
                                        </td>
                                        <td className="p-2 text-right">
                                          <div className="flex justify-end gap-1">
                                            {editingPayment === `${debt.id}-${payment.payment_number}` ? (
                                              <>
                                                <Button size="sm" onClick={() => handleUpdatePayment(debt.id, payment.payment_number)} className="h-7 bg-green-600 hover:bg-green-700 text-white">
                                                  <Check className="h-3 w-3" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => { setEditingPayment(null); setEditPaymentData({}); }} className="h-7">Annuler</Button>
                                              </>
                                            ) : (
                                              <>
                                                <Button variant="ghost" size="sm" onClick={() => { setEditingPayment(`${debt.id}-${payment.payment_number}`); setEditPaymentData({}); }} className="h-7 text-gray-500 hover:text-black">
                                                  <Edit className="h-3 w-3" />
                                                </Button>
                                                {payment.status !== 'paid' && (
                                                  <Button variant="ghost" size="sm" onClick={() => handleMarkPaymentPaid(debt.id, payment.payment_number, payment.amount)} className="h-7 text-green-600 hover:text-green-700">
                                                    <Check className="h-3 w-3" />
                                                  </Button>
                                                )}
                                                <Button variant="ghost" size="sm" onClick={() => handleDeletePayment(debt.id, payment.payment_number)} className="h-7 text-red-500 hover:text-red-700">
                                                  <Trash2 className="h-3 w-3" />
                                                </Button>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Quebec Taxes Tab */}
            <TabsContent value="taxes">
              <div className="space-y-6">
                {/* Fiscal Year Selector */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Label className="text-gray-700">Année fiscale:</Label>
                    <Select value={selectedFiscalYear.toString()} onValueChange={(v) => setSelectedFiscalYear(parseInt(v))}>
                      <SelectTrigger className="w-32 bg-white border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {fiscalYears.map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-gray-500 text-sm">(1er nov. {selectedFiscalYear - 1} au 31 oct. {selectedFiscalYear})</span>
                  </div>
                  <Dialog open={addTaxOpen} onOpenChange={setAddTaxOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-black text-white hover:bg-gray-800" data-testid="add-tax-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Nouvelle entrée
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-gray-200">
                      <DialogHeader>
                        <DialogTitle className="text-black">Ajouter une entrée de taxe</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label className="text-gray-700">Type</Label>
                          <Select value={newTax.entry_type} onValueChange={(v) => setNewTax({ ...newTax, entry_type: v })}>
                            <SelectTrigger className="bg-white border-gray-300">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              <SelectItem value="collected">Taxe récoltée (ventes)</SelectItem>
                              <SelectItem value="paid">Taxe payée (achats)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-700">Description *</Label>
                          <Input value={newTax.description} onChange={(e) => setNewTax({ ...newTax, description: e.target.value })} className="bg-white border-gray-300 text-black" placeholder="Ex: Vente équipement client ABC" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-700">Montant avant taxes *</Label>
                            <Input type="number" step="0.01" value={newTax.subtotal || ''} onChange={(e) => setNewTax({ ...newTax, subtotal: parseFloat(e.target.value) || 0 })} className="bg-white border-gray-300 text-black" placeholder="0.00" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700">Date</Label>
                            <Input type="date" value={newTax.date} onChange={(e) => setNewTax({ ...newTax, date: e.target.value })} className="bg-white border-gray-300 text-black" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-700">Référence (facture)</Label>
                          <Input value={newTax.reference} onChange={(e) => setNewTax({ ...newTax, reference: e.target.value })} className="bg-white border-gray-300 text-black" placeholder="Ex: FAC-2024-001" />
                        </div>
                        
                        {newTax.subtotal > 0 && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-2">Calcul automatique des taxes:</p>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">TPS (5%):</span>
                                <span className="ml-2 font-mono">{formatPrice(taxPreview.tps)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">TVQ (9.975%):</span>
                                <span className="ml-2 font-mono">{formatPrice(taxPreview.tvq)}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Total:</span>
                                <span className="ml-2 font-mono font-semibold">{formatPrice(taxPreview.total)}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline" className="border-gray-300">Annuler</Button>
                        </DialogClose>
                        <Button onClick={handleAddTax} disabled={addingTax} className="bg-black text-white hover:bg-gray-800">
                          {addingTax && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Ajouter
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Quarterly Summary */}
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-black">Résumé trimestriel - Année fiscale {selectedFiscalYear}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {taxSummary ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="text-left p-3 text-gray-600">Trimestre</th>
                              <th className="text-right p-3 text-gray-600">TPS récoltée</th>
                              <th className="text-right p-3 text-gray-600">TVQ récoltée</th>
                              <th className="text-right p-3 text-gray-600">TPS payée</th>
                              <th className="text-right p-3 text-gray-600">TVQ payée</th>
                              <th className="text-right p-3 text-gray-600 bg-yellow-50">Net à payer</th>
                            </tr>
                          </thead>
                          <tbody>
                            {taxSummary.quarters.map((q) => (
                              <tr key={q.quarter} className="border-b border-gray-100">
                                <td className="p-3 font-medium text-black">{q.name}</td>
                                <td className="p-3 text-right font-mono text-green-600">{formatPrice(q.collected_tps)}</td>
                                <td className="p-3 text-right font-mono text-green-600">{formatPrice(q.collected_tvq)}</td>
                                <td className="p-3 text-right font-mono text-red-600">{formatPrice(q.paid_tps)}</td>
                                <td className="p-3 text-right font-mono text-red-600">{formatPrice(q.paid_tvq)}</td>
                                <td className="p-3 text-right font-mono font-semibold bg-yellow-50 text-black">{formatPrice(q.net_total)}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 font-semibold">
                              <td className="p-3 text-black">TOTAL</td>
                              <td className="p-3 text-right font-mono text-green-600">{formatPrice(taxSummary.totals.collected_tps)}</td>
                              <td className="p-3 text-right font-mono text-green-600">{formatPrice(taxSummary.totals.collected_tvq)}</td>
                              <td className="p-3 text-right font-mono text-red-600">{formatPrice(taxSummary.totals.paid_tps)}</td>
                              <td className="p-3 text-right font-mono text-red-600">{formatPrice(taxSummary.totals.paid_tvq)}</td>
                              <td className="p-3 text-right font-mono font-bold bg-yellow-100 text-black">{formatPrice(taxSummary.totals.net_total)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <Skeleton className="h-48 bg-gray-100" />
                    )}
                  </CardContent>
                </Card>

                {/* Tax Entries List */}
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-black">Entrées de taxes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {taxEntries.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucune entrée de taxe</p>
                    ) : (
                      <div className="space-y-2">
                        {taxEntries.filter(t => t.fiscal_year === selectedFiscalYear).map((entry) => (
                          <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${entry.entry_type === 'collected' ? 'bg-green-100' : 'bg-red-100'}`}>
                                <Receipt className={`h-5 w-5 ${entry.entry_type === 'collected' ? 'text-green-700' : 'text-red-700'}`} />
                              </div>
                              <div>
                                <p className="font-medium text-black">{entry.description}</p>
                                <div className="flex items-center gap-2 text-gray-500 text-xs">
                                  <span>{formatDate(entry.date)}</span>
                                  {entry.reference && <span>• {entry.reference}</span>}
                                  <Badge className={entry.entry_type === 'collected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                    {entry.entry_type === 'collected' ? 'Récoltée' : 'Payée'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-mono text-black">{formatPrice(entry.total_taxes)}</p>
                                <p className="text-xs text-gray-500">TPS: {formatPrice(entry.tps_amount)} • TVQ: {formatPrice(entry.tvq_amount)}</p>
                              </div>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteTax(entry.id)}>
                                <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-600" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Expenses Tab */}
            <TabsContent value="expenses">
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-black">Dépenses</CardTitle>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-black text-white hover:bg-gray-800" data-testid="add-expense-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-gray-200">
                      <DialogHeader>
                        <DialogTitle className="text-black">Nouvelle dépense</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label className="text-gray-700">Responsable</Label>
                          <Input value={newExpense.owner} onChange={(e) => setNewExpense({ ...newExpense, owner: e.target.value })} className="bg-white border-gray-300 text-black" placeholder="Nom" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-700">Description</Label>
                          <Input value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} className="bg-white border-gray-300 text-black" placeholder="Description" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-700">Montant</Label>
                            <Input type="number" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) })} className="bg-white border-gray-300 text-black" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700">Date</Label>
                            <Input type="date" value={newExpense.date} onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })} className="bg-white border-gray-300 text-black" />
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button onClick={handleAddExpense} disabled={addingExpense} className="bg-black text-white hover:bg-gray-800">
                          {addingExpense && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Ajouter
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {expenses.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Aucune dépense</p>
                  ) : (
                    <div className="space-y-2">
                      {expenses.map((expense) => (
                        <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-black">{expense.description}</p>
                            <p className="text-gray-500 text-xs">{expense.owner} • {formatDate(expense.date)}</p>
                          </div>
                          <p className="font-mono text-black">{formatPrice(expense.amount)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Budgets Tab */}
            <TabsContent value="budgets">
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black">Budgets</CardTitle>
                </CardHeader>
                <CardContent>
                  {budgets.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Aucun budget</p>
                  ) : (
                    <div className="space-y-2">
                      {budgets.map((budget) => (
                        <div key={budget.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-black">{budget.name}</p>
                            <p className="text-gray-500 text-xs">{budget.category}</p>
                          </div>
                          <p className="font-mono text-black">{formatPrice(budget.amount)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
